import React, { Fragment } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { Box, Grid, Stack, Text } from '@atlaskit/primitives/compiled';
import { TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';
import type { RankedDocumentWithChunks } from '../types';

import { Panel } from './panel-wrapper';

const styles = cssMap({
	rankedDocsGrid: {
		gridTemplateColumns: '50px 1fr 80px 80px 80px',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
	},
	gridHeader: {
		backgroundColor: token('color.background.neutral'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		borderRightColor: token('color.border'),
	},
	gridCell: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		borderRightColor: token('color.border'),
	},
	gridUrlCell: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		borderRightColor: token('color.border'),
		wordBreak: 'break-all',
	},
});

type RankedSearchResultsPanelProps = {
	rankedDocumentsWithChunks: RankedDocumentWithChunks[];
};

export const RankedSearchResultsPanel: ({ rankedDocumentsWithChunks, }: RankedSearchResultsPanelProps) => React.JSX.Element = ({
	rankedDocumentsWithChunks,
}: RankedSearchResultsPanelProps) => {
	const { formatMessage } = useIntl();

	return (
		<TabPanel>
			<Panel testId="sain-online-debugger-ranked-search-results-panel">
				{rankedDocumentsWithChunks.length === 0 ? (
					<Text>{formatMessage(messages.ai_debugger_modal_no_ranked_search_results)}</Text>
				) : (
					<Stack space="space.050">
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_ranked_search_results_heading, {
								count: rankedDocumentsWithChunks.length,
							})}
						</Heading>
						<Grid xcss={styles.rankedDocsGrid}>
							{/* Header row */}
							<Box xcss={styles.gridHeader}>
								<Text weight="semibold">
									{formatMessage(messages.ai_debugger_modal_table_doc_index_header)}
								</Text>
							</Box>
							<Box xcss={styles.gridHeader}>
								<Text weight="semibold">
									{formatMessage(messages.ai_debugger_modal_table_url_header)}
								</Text>
							</Box>
							<Box xcss={styles.gridHeader}>
								<Text weight="semibold">
									{formatMessage(messages.ai_debugger_modal_table_chunk_id_header)}
								</Text>
							</Box>
							<Box xcss={styles.gridHeader}>
								<Text weight="semibold">
									{formatMessage(messages.ai_debugger_modal_table_chunk_score_header)}
								</Text>
							</Box>
							<Box xcss={styles.gridHeader}>
								<Text weight="semibold">
									{formatMessage(messages.ai_debugger_modal_table_chunk_rank_header)}
								</Text>
							</Box>

							{/* Data rows */}
							{rankedDocumentsWithChunks.map((doc, index) => {
								const chunkCount = Math.max(doc.chunks.length, 1);

								return (
									<Fragment key={`${doc.url}-${index}`}>
										{/* Index cell spanning multiple rows */}
										<Box
											xcss={styles.gridCell}
											style={{
												gridRow: `span ${chunkCount}`,
											}}
										>
											<Text weight="semibold">{index + 1}</Text>
										</Box>
										{/* URL cell spanning multiple rows */}
										<Box
											xcss={styles.gridUrlCell}
											style={{
												gridRow: `span ${chunkCount}`,
											}}
										>
											<Link href={doc.url} target="_blank">
												{doc.url}
											</Link>
										</Box>

										{/* Chunk cells */}
										{doc.chunks.length > 0 ? (
											doc.chunks.map((chunk, chunkIndex) => (
												<Fragment key={`${chunk.chunk_id}-${chunkIndex}`}>
													<Box xcss={styles.gridCell}>
														<Text>{chunk.chunk_id}</Text>
													</Box>
													<Box xcss={styles.gridCell}>
														<Text>{chunk.score.toFixed(4)}</Text>
													</Box>
													<Box xcss={styles.gridCell}>
														<Text>{chunk.rank}</Text>
													</Box>
												</Fragment>
											))
										) : (
											<Fragment>
												<Box xcss={styles.gridCell}>
													<Text color="color.text.subtlest">
														{formatMessage(messages.ai_debugger_modal_unable_to_match_chunks)}
													</Text>
												</Box>
												<Box xcss={styles.gridCell}>
													<Text>-</Text>
												</Box>
												<Box xcss={styles.gridCell}>
													<Text>-</Text>
												</Box>
											</Fragment>
										)}
									</Fragment>
								);
							})}
						</Grid>
					</Stack>
				)}
			</Panel>
		</TabPanel>
	);
};
